import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Mail, FileCheck, CheckCircle, Zap, BarChart3, Users, AlertCircle, Clock } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const TenderLens = () => {
  const heroRef = useScrollAnimation({ threshold: 0.2 });
  const statsRef = useScrollAnimation({ threshold: 0.2 });
  const featuresRef = useScrollAnimation({ threshold: 0.2 });
  const benefitsRef = useScrollAnimation({ threshold: 0.2 });
  const ctaRef = useScrollAnimation({ threshold: 0.3 });

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-12 lg:py-16 overflow-hidden" ref={heroRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
            <div className="max-w-2xl">
              <div className={`inline-flex items-center justify-center p-3 bg-amber-100 rounded-2xl mb-6 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
                <Search className="text-amber-600" size={32} />
              </div>
              <h1 className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: heroRef.isVisible ? '0.1s' : undefined }}>
                TenderLens AI
              </h1>
              <p className={`text-xl text-gray-600 mb-8 leading-relaxed transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: heroRef.isVisible ? '0.2s' : undefined }}>
                Intelligent tender discovery and management platform that automatically identifies, analyzes, and manages tender opportunities with AI-powered precision and compliance tracking.
              </p>
              <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: heroRef.isVisible ? '0.3s' : undefined }}>
                <Link to="/contact" className="px-8 py-3 bg-amber-600 text-white rounded-full font-semibold hover:bg-amber-700 transition-colors text-center">
                  Request Demo
                </Link>
                <button
                  onClick={() => featuresRef.ref.current?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-3 bg-white text-gray-700 border border-gray-200 rounded-full font-semibold hover:bg-gray-50 transition-colors"
                >
                  View Features
                </button>
              </div>
            </div>
            <div className={`relative transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: heroRef.isVisible ? '0.4s' : undefined }}>
              <div className="absolute inset-0 bg-amber-600 rounded-3xl rotate-3 opacity-10"></div>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=2070"
                alt="Tender Document Analysis"
                className="relative rounded-3xl shadow-2xl border-4 border-white transform hover:-translate-y-2 transition-transform duration-500 hover:scale-[1.02]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-gray-100" ref={statsRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className={`transition-all duration-1000 ${statsRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: statsRef.isVisible ? '0s' : undefined }}>
              <div className="text-4xl font-bold text-amber-600 mb-2">95%</div>
              <p className="text-gray-600">Match Accuracy with AI Scoring</p>
            </div>
            <div className={`transition-all duration-1000 ${statsRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: statsRef.isVisible ? '0.1s' : undefined }}>
              <div className="text-4xl font-bold text-amber-600 mb-2">15+</div>
              <p className="text-gray-600">Critical Details Auto-Extracted</p>
            </div>
            <div className={`transition-all duration-1000 ${statsRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: statsRef.isVisible ? '0.2s' : undefined }}>
              <div className="text-4xl font-bold text-amber-600 mb-2">Real-time</div>
              <p className="text-gray-600">Tender Notifications</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-gray-50" ref={featuresRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 transition-all duration-1000 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Core Capabilities
            </h2>
            <p className={`text-lg text-gray-600 max-w-2xl mx-auto transition-all duration-1000 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.1s' : undefined }}>
              Streamline your tender management process with intelligent automation and real-time insights.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className={`bg-white rounded-2xl p-8 shadow-lg border border-transparent hover:border-amber-300 transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0s' : undefined }}>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <Mail className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Automated Tender Discovery</h3>
              <p className="text-gray-600 mb-4">
                Continuously monitor multiple databases and portals. Get intelligent notifications when matching opportunities are released with 95% accuracy scoring.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Multi-source monitoring</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Smart profile matching</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Instant notifications</li>
              </ul>
            </div>

            {/* Feature 2 */}
            <div className={`bg-white rounded-2xl p-8 shadow-lg border border-transparent hover:border-amber-300 transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.1s' : undefined }}>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <FileCheck className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Document Analysis</h3>
              <p className="text-gray-600 mb-4">
                Automatically extract 15+ critical details from tender documents including deadlines, values, and requirements with OCR capabilities.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Auto-extraction of details</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> OCR for scanned docs</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Requirement identification</li>
              </ul>
            </div>

            {/* Feature 3 */}
            <div className={`bg-white rounded-2xl p-8 shadow-lg border border-transparent hover:border-amber-300 transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.2s' : undefined }}>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Interactive Q&A on Tenders</h3>
              <p className="text-gray-600 mb-4">
                Ask questions about tender documents and get instant answers. Semantic search across all tender files with source references.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Instant answers</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Semantic search</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Source references</li>
              </ul>
            </div>

            {/* Feature 4 */}
            <div className={`bg-white rounded-2xl p-8 shadow-lg border border-transparent hover:border-amber-300 transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.3s' : undefined }}>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Checklist</h3>
              <p className="text-gray-600 mb-4">
                Auto-generated requirement checklists for each tender. Track compliance status and identify missing documents automatically.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Auto-generated lists</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Document tracking</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Compliance scoring</li>
              </ul>
            </div>

            {/* Feature 5 */}
            <div className={`bg-white rounded-2xl p-8 shadow-lg border border-transparent hover:border-amber-300 transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.4s' : undefined }}>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Workflow & Case Management</h3>
              <p className="text-gray-600 mb-4">
                Organize tenders into cases for team investigation. Assign cases to departments and track status through investigation workflows.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Case organization</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Team assignment</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Status tracking</li>
              </ul>
            </div>

            {/* Feature 6 */}
            <div className={`bg-white rounded-2xl p-8 shadow-lg border border-transparent hover:border-amber-300 transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.5s' : undefined }}>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="text-amber-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Analytics & Insights</h3>
              <p className="text-gray-600 mb-4">
                Get real-time visibility into your tender pipeline. Track win rates, response times, and bid success metrics across all opportunities.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Real-time analytics</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Win rate tracking</li>
                <li className="flex items-center gap-2"><CheckCircle size={16} className="text-amber-500" /> Performance metrics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" ref={benefitsRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center transition-all duration-1000 ${benefitsRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>Why TenderLens AI?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className={`flex gap-6 transition-all duration-1000 ${benefitsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: benefitsRef.isVisible ? '0.1s' : undefined }}>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="text-amber-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Save Time</h3>
                <p className="text-gray-600">
                  Eliminate manual tender searching and document review. Reduce bid preparation time from weeks to days with intelligent automation.
                </p>
              </div>
            </div>

            <div className={`flex gap-6 transition-all duration-1000 ${benefitsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: benefitsRef.isVisible ? '0.2s' : undefined }}>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Zap className="text-amber-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Never Miss Opportunities</h3>
                <p className="text-gray-600">
                  Automatic notifications ensure you're immediately aware of matching tenders. Stay ahead of competition with instant alerts.
                </p>
              </div>
            </div>

            <div className={`flex gap-6 transition-all duration-1000 ${benefitsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: benefitsRef.isVisible ? '0.3s' : undefined }}>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="text-amber-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Reduce Risk</h3>
                <p className="text-gray-600">
                  AI-powered analysis catches critical details, requirements, and compliance needs. Avoid costly bid mistakes and disqualifications.
                </p>
              </div>
            </div>

            <div className={`flex gap-6 transition-all duration-1000 ${benefitsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: benefitsRef.isVisible ? '0.4s' : undefined }}>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="text-amber-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Increase Win Rate</h3>
                <p className="text-gray-600">
                  Faster bid responses with streamlined document analysis. Focus on strategy while TenderLens AI handles the heavy lifting.
                </p>
              </div>
            </div>

            <div className={`flex gap-6 transition-all duration-1000 ${benefitsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: benefitsRef.isVisible ? '0.5s' : undefined }}>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Users className="text-amber-600" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scale Effortlessly</h3>
                <p className="text-gray-600">
                  Manage hundreds of tenders without additional staff overhead. Grow your tender business with the same team size.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-amber-600 to-orange-600 text-white" ref={ctaRef.ref}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 transition-all duration-1000 ${ctaRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>
            Transform Your Tender Management Today
          </h2>
          <p className={`text-lg mb-8 opacity-90 max-w-2xl mx-auto transition-all duration-1000 ${ctaRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: ctaRef.isVisible ? '0.1s' : undefined }}>
            Join forward-thinking organizations using TenderLens AI to streamline tender discovery, accelerate bid processes, and increase win rates.
          </p>
          <Link
            to="/contact"
            className={`inline-block px-10 py-4 bg-white text-amber-600 rounded-full font-bold hover:bg-gray-100 transition-all duration-1000 ${ctaRef.isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ transitionDelay: ctaRef.isVisible ? '0.2s' : undefined }}
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TenderLens;

